// Translate Static Image's form (live's `TranslateStaticImageForm`, spec G14-templates): the
// creative and its target languages; one tile a language, on live's fixed model.
import { Group, Popover, Stack, Text, TextInput, Tooltip, UnstyledButton } from "@mantine/core";
import { Check, Languages } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useGetCatalog } from "../../../api/generated/endpoints/generation/generation";
import { TranslationLanguage } from "../../../api/generated/model";
import { ImageTemplateQuote, ImageTemplateRequest } from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { GlassPanel } from "../../../ui/GlassPanel";
import { Pill } from "../../../ui/Pill";
import {
  exampleImage,
  ImageSlot,
  ReuseSwitch,
  slotSource,
  StartArrow,
  type FormExample,
  type SlotImage,
  type TemplateFormProps,
} from "./kit";
import { CostChip } from "../TemplateCost";
import classes from "./forms.module.css";

/** Live's fixed model (`GPT_IMAGE_MODEL_ID`), by our catalog id, and its words when it is gone. */
const MODEL = "gpt-image-2.5-sunburst";
const MODEL_MISSING = "The image model GPT-2.5-SUNBURST is not available.";
/** Live's list, in live's order (`TRANSLATION_LANGUAGES`). */
const LANGUAGES = Object.values(TranslationLanguage);
/** The bound of the language typed under "Other" (the route's). */
const CUSTOM_MAX = 50;

interface Picked {
  selected: TranslationLanguage[];
  custom: string;
}

/** Live's `getTargetLanguages`: the ticked ones in live's order, then the typed one unless repeated. */
export function targetLanguages({ selected, custom }: Picked): string[] {
  const ticked = LANGUAGES.filter((each) => selected.includes(each));
  const typed = custom.trim();
  const repeat = ticked.some((each) => each.toLowerCase() === typed.toLowerCase());
  return typed !== "" && !repeat ? [...ticked, typed] : ticked;
}

/** Live's trigger words: the placeholder, the one language, or "N languages". */
function languagesLabel(languages: readonly string[]): string {
  if (languages.length === 0) return "Select languages";
  if (languages.length === 1) return languages[0]!;
  return `${languages.length} languages`;
}

/**
 * Live's languages picker (`LanguagesPopover`): the list, each ticked or not, and "Other", which
 * turns into a field for one language the list lacks.
 */
function LanguagesPicker(props: { value: Picked; onChange: (value: Picked) => void }) {
  const { value, onChange } = props;
  const [typing, setTyping] = useState(false);
  const field = useRef<HTMLInputElement>(null);
  // "Other" turns into its field with the caret in it, as live's.
  useEffect(() => {
    if (typing) field.current?.focus();
  }, [typing]);
  const label = languagesLabel(targetLanguages(value));
  return (
    <Popover position="top-start" width={189} withinPortal={false} onClose={() => setTyping(false)}>
      <Tooltip label="Target languages" withArrow>
        <Popover.Target>
          <Pill icon={<Languages size={16} />} label={label} aria-label={`Target languages: ${label}`} />
        </Popover.Target>
      </Tooltip>
      <Popover.Dropdown p={4}>
        <Stack gap={2}>
          {LANGUAGES.map((language) => {
            const ticked = value.selected.includes(language);
            return (
              <UnstyledButton
                key={language}
                className={classes.option}
                aria-pressed={ticked}
                onClick={() =>
                  onChange({
                    ...value,
                    selected: ticked ? value.selected.filter((each) => each !== language) : [...value.selected, language],
                  })
                }
              >
                <span>{language}</span>
                {ticked ? <Check size={16} aria-hidden /> : null}
              </UnstyledButton>
            );
          })}
          {typing || value.custom !== "" ? (
            <TextInput
              ref={field}
              aria-label="Other"
              placeholder="Add language"
              maxLength={CUSTOM_MAX}
              value={value.custom}
              onChange={(event) => {
                setTyping(true);
                onChange({ ...value, custom: event.currentTarget.value });
              }}
            />
          ) : (
            <UnstyledButton className={classes.option} onClick={() => setTyping(true)}>
              Other
            </UnstyledButton>
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

const NO_LANGUAGES: Picked = { selected: [], custom: "" };

interface Values {
  image: SlotImage | null;
  languages: Picked;
}

/** The form's values: the card's example's (its creative and live's five languages), or empty. */
function valuesOf(example: FormExample | null): Values | null {
  const inputs = example?.inputs;
  if (example === null || inputs?.template !== "translate-static-image") return null;
  return {
    image: exampleImage(example, inputs.image),
    languages: { selected: [...inputs.languages.selected], custom: inputs.languages.custom ?? "" },
  };
}

/**
 * Live's form: "Reuse example" (on fills the card's example, off clears the form; Replicate opens
 * it on), the "Image" slot ("Pick the creative to translate"), and in the footer the languages
 * picker and the round start, off until both are there and while live's model is not offered.
 */
export function TranslateImageForm(props: TemplateFormProps) {
  const { teamId, brandId, above, example, onStarted } = props;
  const [collapsed, setCollapsed] = useState(false);
  const [start] = useState(() => (props.replicate ? valuesOf(example) : null));
  const [reuse, setReuse] = useState(start !== null);
  const [image, setImage] = useState<SlotImage | null>(start?.image ?? null);
  const [languages, setLanguages] = useState<Picked>(start?.languages ?? NO_LANGUAGES);
  const fill = (on: boolean) => {
    const values = on ? valuesOf(example) : null;
    setReuse(values !== null);
    setImage(values?.image ?? null);
    setLanguages(values?.languages ?? NO_LANGUAGES);
  };
  const catalog = useGetCatalog().data;
  const missing = catalog !== undefined && !catalog.image_models.some((each) => each.id === MODEL);
  const inputs =
    image === null || targetLanguages(languages).length === 0
      ? null
      : {
          template: "translate-static-image" as const,
          image: slotSource(image),
          languages,
        };
  const body = inputs === null ? null : { type: "image.template" as const, inputs };
  const request = ImageTemplateRequest.safeParse(body);
  const quote = ImageTemplateQuote.safeParse(body);
  const job = usePricedStart({
    teamId,
    brandId,
    quote: quote.success && !missing ? quote.data : undefined,
    request: request.success && !missing ? request.data : undefined,
    onStarted,
  });
  return (
    <GlassPanel
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      above={above}
      header={<ReuseSwitch example={example} checked={reuse} onChange={fill} />}
      summary={<Text size="sm">Translate Static Image</Text>}
      footer={
        <Group justify="space-between" wrap="nowrap" gap={12}>
          <LanguagesPicker value={languages} onChange={setLanguages} />
          <Group gap={8} wrap="nowrap">
            <CostChip estimate={props.template.estimate} outputs={targetLanguages(languages).length} />
            <StartArrow canStart={job.canStart} starting={job.start.isPending} onStart={job.startJob} />
          </Group>
        </Group>
      }
    >
      <Stack gap={12}>
        <ImageSlot
          teamId={teamId}
          brandId={brandId}
          label="Image"
          title="Pick the creative to translate"
          value={image}
          onChange={setImage}
        />
        {missing ? (
          <Text size="sm" c="var(--cai-color-text-danger)" role="alert">
            {MODEL_MISSING}
          </Text>
        ) : null}
        {job.quoteError === null ? null : <ErrorAlert error={job.quoteError} />}
        {job.start.isError ? <ErrorAlert error={job.start.error} /> : null}
        {/* Live's toast, said in the panel (web/AGENTS.md: no toasts). */}
        {job.start.isSuccess ? (
          <Text component="output" size="sm">
            Generation started
          </Text>
        ) : null}
      </Stack>
    </GlassPanel>
  );
}
